
import React, { useState, useEffect } from 'react';
import { usePartners } from '@/hooks/usePartners';
import PartnerCard from './PartnerCard';
import DomainList from './DomainList';
import { Association, PartnerWithDetails, Domain } from '@/types/partner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';

interface PartnerListingProps {
  associationType: Association['name'];
  showDomains?: boolean;
  partnersData?: PartnerWithDetails[]; // Add optional prop to receive partners data directly
}

const PartnerListing = ({ 
  associationType, 
  showDomains = false,
  partnersData 
}: PartnerListingProps) => {
  const [selectedDomainId, setSelectedDomainId] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [partners, setPartners] = useState<PartnerWithDetails[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  
  const { getPartnersByType, loading, error } = usePartners();
  
  // Log to debug the component's data
  useEffect(() => {
    console.log('PartnerListing received props:', { associationType, showDomains, partnersData });
    console.log('Current partners data in listing:', partners);
  }, [associationType, showDomains, partnersData, partners]);
  
  // Fetch partners data if not provided via props
  useEffect(() => {
    const fetchPartners = async () => {
      if (!partnersData) {
        console.log('No partners data provided via props, fetching from API...');
        const fetchedPartners = await getPartnersByType(associationType);
        setPartners(fetchedPartners);
        
        // Extract unique domains from all partners
        const allDomains: Domain[] = [];
        fetchedPartners.forEach(partner => {
          partner.domains?.forEach(domain => {
            if (!allDomains.find(d => d.id === domain.id)) {
              allDomains.push(domain);
            }
          });
        });
        
        setDomains(allDomains);
      } else {
        console.log('Using partners data provided via props:', partnersData.length);
        setPartners(partnersData);
        
        // Extract unique domains from provided partners data
        const allDomains: Domain[] = [];
        partnersData.forEach(partner => {
          partner.domains?.forEach(domain => {
            if (!allDomains.find(d => d.id === domain.id)) {
              allDomains.push(domain);
            }
          });
        });
        
        setDomains(allDomains);
      }
    };
    
    fetchPartners();
  }, [associationType, partnersData, getPartnersByType]);
  
  // Filter partners based on search query and selected domain
  const filteredPartners = partners.filter(partner => {
    // Filter by search query
    const matchesSearch = 
      partner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (partner.education && partner.education.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (partner.about_us && partner.about_us.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Filter by domain if one is selected
    const matchesDomain = !selectedDomainId || 
      partner.domains?.some(domain => domain.id === selectedDomainId);
    
    return matchesSearch && matchesDomain;
  });
  
  console.log('Filtered partners:', filteredPartners.length);
  
  return (
    <div className="container mx-auto py-8">
      <div className="flex flex-col md:flex-row gap-8">
        {/* Domains sidebar - only shown for Domain Experts */}
        {showDomains && (
          <div className="md:w-1/4">
            <div className="sticky top-24">
              <h2 className="text-xl font-semibold mb-4">Domains</h2>
              <Button 
                variant="outline" 
                className="mb-4 w-full justify-start"
                onClick={() => setSelectedDomainId(undefined)}
              >
                All Domains
              </Button>
              <DomainList 
                domains={domains} 
                onSelectDomain={setSelectedDomainId} 
              />
            </div>
          </div>
        )}
        
        {/* Main content */}
        <div className={showDomains ? "md:w-3/4" : "w-full"}>
          <div className="flex flex-col gap-4 mb-8">
            <h1 className="text-3xl font-bold">{associationType}s</h1>
            
            {/* Search bar */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                type="text"
                placeholder={`Search ${associationType}s...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          {loading ? (
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="h-64 rounded-md bg-muted animate-pulse" />
              ))}
            </div>
          ) : filteredPartners.length > 0 ? (
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredPartners.map(partner => (
                <PartnerCard 
                  key={partner.id} 
                  partner={partner} 
                  associationType={associationType}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-muted-foreground">
                No {associationType}s found
              </h3>
              <p className="mt-2">
                {searchQuery ? 
                  "Try adjusting your search query." : 
                  selectedDomainId ? 
                    "No experts available in this domain." : 
                    "Check back later for available experts."
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerListing;
